import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'astroturf/react';

const TabHeader = styled.div<{ overflowmode: TabProps['overflowMode'] }>`
  display: flex;
  column-gap: 16px;

  &.overflowmode-hide {
    overflow-x: hidden;
  }

  &.overflowmode-scroll {
    overflow-x: auto;
  }

  &.overflowmode-wrap {
    flex-wrap: wrap;
  }
`;

const TabTitle = styled.button<{ active: boolean }>`
  @import 'stylesheets/design_system/font';
  @include sans-serif;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;

  height: 38px;
  padding: 0 16px;
  color: $ds-gray-dark;
  background-color: transparent;
  white-space: nowrap;
  border: none;
  border-bottom: 3px solid transparent;
  border-top: 3px solid transparent;
  outline: none;
  cursor: pointer;

  &:not(:disabled) {
    &:focus-visible {
      padding: 0 13px;
      color: $ds-primary-blue-active;
      border: 3px solid #cddaef;
    }
  }

  &:not(.active):not(:disabled) {
    &:hover {
      color: $ds-primary-blue-active;
      border-bottom: 3px solid #b4c7e8;
    }

    &:active {
      color: #082f75;
      border-bottom: 3px solid #9bb5e0;
    }
  }

  &.active:not(:disabled) {
    color: $ds-primary-blue-active;
    border-bottom: 3px solid #0645b1;

    &:hover {
      color: #082f75;
      border-bottom: 3px solid #0645b1;
    }

    &:active {
      color: #043059;
      border-bottom: 3px solid #0645b1;
    }
  }

  &:disabled {
    color: #b1b1ba;
    border-bottom: 3px solid #ebebee;
  }
`;

const TabDivider = styled.div`
  margin-top: -1px;
  border-top: 1px solid #ebebee;
  width: 100%;
`;

const TabContent = styled.div<{ active: boolean }>`
  display: none;

  &.active {
    display: block;
  }
`;

export type PersistMode = 'query-param';

export type RenderMode = 'always' | 'active' | 'lazy';

export type OverflowMode = 'hide' | 'scroll' | 'wrap';

export type Tab = {
  /**
   * A unique key used to identify this tab.
   * It will also be used for saving to query params and other places.
   */
  key: string;
  /**
   * Render the tab title.
   * @param tab The current tab. This parameter is useful if you want a generic
   *   callback for all tabs.
   * @param index The current tab index.
   * @param active Whether this tab is currently active.
   */
  renderTitle: (tab: Tab, index: number, active: boolean) => ReactNode;
  /**
   * Render the tab content.
   * @param tab The current tab.
   * @param index The current tab index.
   * @param active Whether this tab is currently active.
   */
  render: (tab: Tab, index: number, active: boolean) => ReactNode;
};

export type TabProps = {
  /** Optionally control which tab is showing */
  tab?: string;
  /** The list of tabs */
  tabs: Tab[];
  /** Optional header content in addition to tabs */
  header?: ReactNode;
  /**
   * Where to persist the active tab.
   * TODO add more options for persisting (local storage, hash).
   *
   * "query-param": Save with query params like `?tab=<tab_key>`
   */
  persistMode?: PersistMode;
  /** What key to use for the query param persist mode */
  queryParam?: string;
  /**
   * How to render the tab content.
   * This option determine when a tab will render, along with
   * if it will stay rendered (but hidden) when the tab is inactive.
   *
   * "always": Always render, just hide the content of inactive tabs
   * "active": Only render when the tab is active
   * "lazy": Wait until the tab is active to render, then behave like "always" mode
   */
  renderMode?: RenderMode;
  /**
   * How to handle when tabs overflow their container.
   *
   * "hide": Simply hide tabs that overflow.
   * "scroll": Add a scrollbar when tabs overflow.
   * "wrap": Wrap tabs that overflow.
   */
  overflowMode?: OverflowMode;
  /**
   * Callback when the active tab changes.
   * Useful for tracking state or controlling the tabs.
   */
  onTabChange?: (tab: string) => void;
};

/**
 * Get the currently persisted tab depending on the persist method.
 * @param persistMode The persist mode.
 * @param queryParam The query parameter.
 * @return The persisted tab, or undefined if none.
 */
function loadPersistedTab(
  persistMode: PersistMode | undefined,
  queryParam = 'tab',
): string | undefined {
  if (persistMode === 'query-param') {
    const url = new URL(window.location.toString());
    return url.searchParams.get(queryParam) ?? undefined;
  }
  return undefined;
}

/**
 * Persists the tab given the persist method.
 * @param tab The tab to persist.
 * @param persistMode The persist mode to use.
 * @param queryParam The query parameter.
 */
function persistTab(
  tab: string,
  persistMode: PersistMode | undefined,
  queryParam = 'tab',
) {
  if (persistMode === 'query-param') {
    const url = new URL(window.location.toString());
    url.searchParams.set(queryParam, tab);
    window.history.pushState({}, '', url);
  }
}

export function useTabs({
  tab,
  tabs,
  header,
  persistMode,
  queryParam = 'tab',
  renderMode = 'active',
  overflowMode = 'scroll',
  onTabChange = () => null,
}: TabProps) {
  const initialTab =
    tab ?? loadPersistedTab(persistMode, queryParam) ?? tabs[0].key;
  const [internalTab, setInternalTab] = useState(initialTab);
  const [lazyLoaded, setLazyLoaded] = useState<string[]>([initialTab]);

  // A consolidated function to perform the logic of changing a tab
  const updateTab = useCallback(
    (newTab: string, shouldPersist = true) => {
      if (internalTab !== newTab) {
        setInternalTab(newTab);
        if (tab !== newTab) {
          onTabChange(newTab);
        }
        if (!lazyLoaded.includes(newTab)) {
          setLazyLoaded(lazyLoaded.concat([newTab]));
        }
        if (shouldPersist) {
          persistTab(newTab, persistMode, queryParam);
        }
      }
    },
    [tab, persistMode, queryParam, internalTab, lazyLoaded, onTabChange],
  );

  // Keep the internal state synced with controlled components
  useEffect(() => {
    if (tab !== undefined) {
      updateTab(tab);
    }
  }, [tab, updateTab]);

  // Notify parent of initial tab state
  useEffect(() => {
    if (tab === undefined) {
      onTabChange(internalTab);
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch for persist changes
  useEffect(() => {
    if (persistMode === 'query-param') {
      const urlChangeListener = () => {
        const url = new URL(window.location.toString());
        const queryTab = url.searchParams.get(queryParam);
        updateTab(queryTab ?? tabs[0].key, false);
      };
      window.addEventListener('popstate', urlChangeListener);
      return () => window.removeEventListener('popstate', urlChangeListener);
    }
  }, [tabs, persistMode, queryParam, updateTab]);

  const tabHeader = useMemo(() => {
    return (
      <>
        <TabHeader overflowmode={overflowMode}>
          {tabs.map((tab, idx) => {
            const active = tab.key === internalTab;

            return (
              <TabTitle
                key={tab.key}
                active={active}
                onClick={() => updateTab(tab.key)}
              >
                {tab.renderTitle(tab, idx, active)}
              </TabTitle>
            );
          })}

          {header}
        </TabHeader>

        <TabDivider />
      </>
    );
  }, [tabs, header, overflowMode, internalTab, updateTab]);

  const tabContent = useMemo(() => {
    return (
      <>
        {tabs.map((tab, idx) => {
          const active = tab.key === internalTab;
          const shouldRender =
            renderMode === 'always' ||
            (renderMode === 'active' && active) ||
            (renderMode === 'lazy' && lazyLoaded.includes(tab.key));

          return (
            <TabContent key={tab.key} active={active}>
              {shouldRender && tab.render(tab, idx, active)}
            </TabContent>
          );
        })}
      </>
    );
  }, [tabs, renderMode, internalTab, lazyLoaded]);

  return { header: tabHeader, content: tabContent, updateTab: updateTab };
}
